import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Modal, ModalBody, Button, Text, Box } from '@flexisaf/flexibull2';
import Theme from 'utils/theme';
import { darken } from 'polished';
import SuccessImg from 'assets/images/successful-upload.svg';
import {
  selectActiveExportProgress,
  selectExportMonitor,
} from 'redux/direct-download/selector';
import {
  minimizeExportMonitor,
  expandExportMonitor,
  dismissExportMonitor,
} from 'redux/direct-download/actions';
import ExportMinimizedCard from './ExportMinimizedCard';
import {
  ExportModalShell,
  ExportModalHeader,
  ExportHeaderActions,
  HeaderIconButton,
  ExportModalBody,
  ExportModalFooter,
  ExportFooterActions,
  ExportFooterCancel,
  GeneratingProgressBar,
  GeneratingProgressMeta,
  ProgressTrack,
  ProgressFill,
} from './styles';

const GlobalExportMonitor = () => {
  const dispatch = useDispatch();
  const monitor = useSelector(selectExportMonitor);
  const {
    progress = 0,
    complete = false,
    error = null,
  } = useSelector(selectActiveExportProgress) || {};

  const monitorActive = Boolean(monitor?.active);
  const monitorMinimized = Boolean(monitor?.minimized);

  useEffect(() => {
    if (error && monitorActive && monitorMinimized) {
      dispatch(expandExportMonitor());
    }
  }, [error, monitorActive, monitorMinimized, dispatch]);

  if (!monitorActive) {
    return null;
  }

  const clampedProgress = Math.max(0, Math.min(100, progress || 0));
  const hasError = Boolean(error);

  if (monitor.minimized && !hasError) {
    return (
      <ExportMinimizedCard
        progress={clampedProgress}
        complete={complete}
        error={error}
        onExpand={() => dispatch(expandExportMonitor())}
        onDismiss={() => dispatch(dismissExportMonitor())}
      />
    );
  }

  let title = 'Generating...';
  let description =
    'Your data file is currently being generated and will be automatically downloaded';

  if (hasError) {
    title = 'Export failed';
    description = error;
  } else if (complete || clampedProgress >= 100) {
    title = 'Export ready';
    description = 'Your data file is ready and should download automatically.';
  }

  return (
    <Modal
      outerclick={false}
      open
      center={false}
      onClose={() => dispatch(dismissExportMonitor())}
    >
      <ModalBody
        width="40vw"
        style={{
          maxHeight: '90vh',
          padding: 0,
          overflow: 'hidden',
        }}
      >
        <ExportModalShell>
          <ExportModalHeader>
            <ExportHeaderActions>
              {!hasError && (
                <HeaderIconButton
                  type="button"
                  title="Minimize"
                  aria-label="Minimize export modal"
                  onClick={() => dispatch(minimizeExportMonitor())}
                >
                  <i className="saf-resize-small" />
                </HeaderIconButton>
              )}
            </ExportHeaderActions>
            <Text block bold size="25px">
              {title}
            </Text>
            <p style={{ color: hasError ? Theme.PrimaryRed : undefined }}>
              {description}
            </p>
            {!hasError && (
              <Text
                block
                size="14px"
                color={Theme.PrimaryGrey}
                style={{ marginTop: 6 }}
              >
                {clampedProgress}% complete
              </Text>
            )}
          </ExportModalHeader>

          <ExportModalBody>
            <Box align="center" margin="35px auto" pad="0 20px 10px">
              <img
                style={{ width: '100%', maxWidth: '18rem' }}
                src={SuccessImg}
                alt="Applicant export status"
              />
            </Box>
          </ExportModalBody>

          <ExportModalFooter>
            {hasError ? (
              <Box pad="16px 24px 8px">
                <Text block color={Theme.PrimaryRed} size="14px">
                  {error}
                </Text>
              </Box>
            ) : (
              <GeneratingProgressBar>
                <GeneratingProgressMeta>
                  <span>
                    {complete ? 'Download ready' : 'Generating export file'}
                  </span>
                  <span>{`${clampedProgress}%`}</span>
                </GeneratingProgressMeta>
                <ProgressTrack>
                  <ProgressFill value={clampedProgress} />
                </ProgressTrack>
              </GeneratingProgressBar>
            )}

            <ExportFooterActions>
              <Button
                iconRight={<i className="flexibull-right" />}
                width={180}
                height={50}
                onClick={() => dispatch(dismissExportMonitor())}
              >
                {hasError ? 'Close' : 'Complete'}
              </Button>
            </ExportFooterActions>
            <ExportFooterCancel bg={darken(0.05, Theme.PrimaryFade)}>
              <Button
                width={120}
                height={40}
                spaceRight
                color="transparent"
                fontColor={Theme.PrimaryRed}
                pale
                onClick={() => dispatch(dismissExportMonitor())}
              >
                Cancel
              </Button>
            </ExportFooterCancel>
          </ExportModalFooter>
        </ExportModalShell>
      </ModalBody>
    </Modal>
  );
};

export default GlobalExportMonitor;
